// Language list per feature-ui.md's "Language" section — codes/labels match
// the same convention already sketched on the (unmerged) feature/ui-batch-2
// branch's CONFIG.LANGUAGES stub, reused here rather than invented fresh so
// there's one canonical code list across branches. es-419 = Latin American
// Spanish, zh-Hans/zh-Hant = Simplified/Traditional Chinese (BCP 47).
export const LANGUAGES = [
  { code: 'en', label: 'English (United States)', rtl: false },
  { code: 'fr', label: 'Français (France)', rtl: false },
  { code: 'de', label: 'Deutsch (Deutschland)', rtl: false },
  { code: 'hi', label: 'हिन्दी (भारत)', rtl: false },
  { code: 'id', label: 'Indonesia (Indonesia)', rtl: false },
  { code: 'it', label: 'Italiano (Italia)', rtl: false },
  { code: 'ja', label: '日本語 (日本)', rtl: false },
  { code: 'ko', label: '한국어 (대한민국)', rtl: false },
  { code: 'pt', label: 'Português (Brasil)', rtl: false },
  { code: 'es-419', label: 'Español (Latinoamérica)', rtl: false },
  { code: 'es', label: 'Español (España)', rtl: false },
  { code: 'zh-Hans', label: 'Chinese (Simplified)', rtl: false },
  { code: 'zh-Hant', label: 'Chinese (Traditional)', rtl: false },
  { code: 'ar', label: 'Arabic', rtl: true },
  { code: 'vi', label: 'Vietnamese', rtl: false },
  { code: 'ur', label: 'Urdu', rtl: true },
  { code: 'tl', label: 'Tagalog', rtl: false },
  { code: 'tr', label: 'Turkish', rtl: false },
  { code: 'uk', label: 'Ukrainian', rtl: false },
  { code: 'ru', label: 'Russian', rtl: false },
  { code: 'th', label: 'Thai', rtl: false },
  { code: 'nl', label: 'Dutch', rtl: false },
  { code: 'pl', label: 'Polish', rtl: false }
];

export const RTL_CODES = new Set(LANGUAGES.filter((l) => l.rtl).map((l) => l.code));

export function isRTL(code) {
  return RTL_CODES.has(code);
}

const SUPPORTED_CODES = new Set(LANGUAGES.map((l) => l.code));
// Regions whose Spanish variant maps to our es-419 (Latin American) catalog
// rather than es (Spain) — a plain "es" tag with no region falls through to
// Spain, same as leaving the region off any other language.
const LATAM_SPANISH_REGIONS = new Set([
  'mx', 'ar', 'co', 'cl', 'pe', 've', 'ec', 'gt', 'cu', 'bo', 'do', 'hn',
  'py', 'sv', 'ni', 'cr', 'pa', 'uy', 'pr', 'us',
  '419' // UN M49 "Latin America and the Caribbean" — the region subtag our own es-419 code uses
]);
const TRADITIONAL_CHINESE_REGIONS = new Set(['tw', 'hk', 'mo']);

// Maps one BCP-47 tag (e.g. "zh-Hant-TW", "es-MX", "fil") onto one of our
// supported codes — the handful of languages here that don't have a single
// obvious primary-subtag match need their script/region disambiguated.
function mapTag(tag) {
  const lower = tag.toLowerCase();
  const parts = lower.split('-');
  const primary = parts[0];
  if (primary === 'zh') {
    return parts.some((p) => p === 'hant' || TRADITIONAL_CHINESE_REGIONS.has(p)) ? 'zh-Hant' : 'zh-Hans';
  }
  if (primary === 'es') return parts.some((p) => LATAM_SPANISH_REGIONS.has(p)) ? 'es-419' : 'es';
  if (primary === 'fil') return 'tl'; // Filipino — browsers sometimes report "fil" instead of "tl"
  return primary;
}

// Best-effort match of the browser's preferred languages (navigator.languages,
// most-preferred first) against LANGUAGES. Used only as the first-run default
// (store.js's initialLanguage) — an explicit choice already saved to
// tynti_language always takes precedence over this, and this never re-runs
// once that's set. Falls back to English when nothing in the list matches,
// same as an unrecognized stored code already does.
export function detectBrowserLanguage(tags) {
  for (const tag of tags || []) {
    if (typeof tag !== 'string' || !tag) continue;
    const mapped = mapTag(tag);
    if (SUPPORTED_CODES.has(mapped)) return mapped;
  }
  return 'en';
}
